import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Spin,
  Input,
  Space,
  message,
  Select,
  Modal,
  Card,
  Divider,
  Alert,
  Tooltip,
} from "antd";
import {
  LoadingOutlined,
  CopyOutlined,
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  LineOutlined,
} from "@ant-design/icons";
import useNotificationData from "../hooks/useNotificationData";

const SimpleEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      <div className="bg-gray-100 p-3 flex gap-2 flex-wrap border-b border-gray-300">
        <Tooltip title="Bold">
          <Button
            size="small"
            type="text"
            icon={<BoldOutlined />}
            onClick={() => applyFormat("bold")}
          />
        </Tooltip>
        <Tooltip title="Italic">
          <Button
            size="small"
            type="text"
            icon={<ItalicOutlined />}
            onClick={() => applyFormat("italic")}
          />
        </Tooltip>
        <Tooltip title="Underline">
          <Button
            size="small"
            type="text"
            icon={<UnderlineOutlined />}
            onClick={() => applyFormat("underline")}
          />
        </Tooltip>
        <Divider type="vertical" />
        <Tooltip title="Bullet list">
          <Button
            size="small"
            type="text"
            icon={<UnorderedListOutlined />}
            onClick={() => applyFormat("insertUnorderedList")}
          />
        </Tooltip>
        <Tooltip title="Numbered list">
          <Button
            size="small"
            type="text"
            icon={<OrderedListOutlined />}
            onClick={() => applyFormat("insertOrderedList")}
          />
        </Tooltip>
        <Divider type="vertical" />
        <Tooltip title="Line break">
          <Button
            size="small"
            type="text"
            icon={<LineOutlined />}
            onClick={() => applyFormat("insertHTML", "<br/>")}
          />
        </Tooltip>
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="w-full p-4 min-h-64 outline-none overflow-auto"
        style={{
          maxHeight: "400px",
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          fontSize: "16px",
        }}
        suppressContentEditableWarning
      >
        {placeholder}
      </div>
    </div>
  );
};

const NotificationTemplatesManager = () => {
  const {
    templates,
    templatePreview,
    loading,
    eventTypes,
    channels,
    allTemplates,
    templateVariables,
    fetchAllTemplates,
    fetchTemplates,
    updateTemplate,
    previewTemplate,
    fetchVariables,
  } = useNotificationData();

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    body: "",
  });
  const [previewVars, setPreviewVars] = useState({});
  const [previewLoading, setPreviewLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [variablesModalOpen, setVariablesModalOpen] = useState(false);
  const [varSearchText, setVarSearchText] = useState("");

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  useEffect(() => {
    fetchAllTemplates();
    fetchVariables();
  }, [fetchAllTemplates, fetchVariables]);

  useEffect(() => {
    if (eventTypes.length > 0 && !selectedEvent) {
      setSelectedEvent(eventTypes[0]);
    }
  }, [eventTypes, selectedEvent]);

  useEffect(() => {
    if (channels.length > 0 && !selectedChannel) {
      setSelectedChannel(channels[0]);
    }
  }, [channels, selectedChannel]);

  useEffect(() => {
    if (selectedEvent) {
      fetchTemplates(selectedEvent);
      setIsEditing(false);
    }
  }, [selectedEvent, fetchTemplates]);

  useEffect(() => {
    const template = templates.find((t) => t.channel === selectedChannel);
    if (template) {
      setFormData({
        title: template.title,
        body: template.body,
      });
    } else {
      setFormData({
        title: "",
        body: "",
      });
    }
  }, [selectedChannel, templates]);

  const currentTemplate = templates.find((t) => t.channel === selectedChannel);

  const extractVariablesFromTemplate = (text) => {
    const regex = /\{\{(\w+)\}\}/g;
    const matches = [...text.matchAll(regex)];
    const vars = new Set(matches.map((m) => m[1]));
    return Array.from(vars);
  };

  const availableVars = isEditing
    ? extractVariablesFromTemplate(formData.body)
    : extractVariablesFromTemplate(currentTemplate?.body || "");

  const allVarsList = templateVariables.reduce((acc, cat) => {
    return [...acc, ...(cat.variables || []).map((v) => v.key)];
  }, []);

  const getRelevantCategories = () => {
    if (!selectedEvent) return templateVariables;

    const eventUpper = selectedEvent.toUpperCase();

    const isAmbassador = eventUpper.includes("AMBASSADOR") || eventUpper.includes("REFERRAL");
    const isOrder = eventUpper.includes("ORDER") || eventUpper.includes("DELIVERY") || eventUpper.includes("RESTAURANT") || eventUpper.includes("DRIVER") || eventUpper.includes("BIKER");
    const isKYC = eventUpper.includes("KYC") || eventUpper.includes("TIER2");
    const isWallet = eventUpper.includes("WALLET") || eventUpper.includes("TRANSACTION") || eventUpper.includes("BONUS") || eventUpper.includes("FUNDING") || eventUpper.includes("PAYMENT");
    const isSupport = eventUpper.includes("SUPPORT") || eventUpper.includes("SM_") || eventUpper.includes("ASSIGNMENT");
    const isStaff = eventUpper.includes("STAFF") || eventUpper.includes("ADMIN") || eventUpper.includes("SIGNUP");

    return templateVariables.filter((cat) => {
      const catName = cat.category.toLowerCase();

      if (catName.includes("user") || catName.includes("general")) {
        return true;
      }

      if (catName.includes("ambassador") && isAmbassador) return true;
      if ((catName.includes("order") || catName.includes("business") || catName.includes("delivery")) && isOrder) return true;
      if (catName.includes("kyc") && isKYC) return true;
      if (catName.includes("wallet") && isWallet) return true;
      if (catName.includes("support") && isSupport) return true;
      if (catName.includes("staff") && isStaff) return true;

      if (!isAmbassador && !isOrder && !isKYC && !isWallet && !isSupport && !isStaff) {
        return true;
      }

      return false;
    });
  };

  const validateTemplate = () => {
    const errors = [];
    const regex = /\{\{(\w+)\}\}/g;
    const matches = [...formData.body.matchAll(regex)];

    matches.forEach((match) => {
      const varName = match[1];
      if (allVarsList.length > 0 && !allVarsList.includes(varName)) {
        errors.push(
          `Invalid variable: {{${varName}}} is not a recognized variable in the system.`,
        );
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleEdit = (template) => {
    setIsEditing(true);
    setFormData({
      title: template.title,
      body: template.body,
    });
    setValidationErrors([]);
  };

  const handleSave = async () => {
    if (!validateTemplate()) {
      return;
    }
    const result = await updateTemplate(selectedEvent, selectedChannel, {
      title: formData.title,
      body: formData.body,
      formatting: "html",
    });
    if (result.success) {
      message.success("Template saved");
      setIsEditing(false);
    } else {
      message.error("Failed to save template");
    }
  };

  const handleDiscard = () => {
    const template = templates.find((t) => t.channel === selectedChannel);
    if (template) {
      setFormData({
        title: template.title,
        body: template.body,
      });
    } else {
      setFormData({
        title: "",
        body: "",
      });
    }
    setIsEditing(false);
    setValidationErrors([]);
  };

  const handlePreview = async () => {
    const requiredVars = new Set();
    const regex = /\{\{(\w+)\}\}/g;
    const matches = [...formData.body.matchAll(regex)];
    matches.forEach((match) => requiredVars.add(match[1]));

    const missing = [...requiredVars].filter((v) => !previewVars[v]);
    if (missing.length > 0) {
      message.warning(`Missing required variables: ${missing.join(", ")}`);
      return;
    }

    setPreviewLoading(true);
    await previewTemplate(selectedEvent, selectedChannel, previewVars);
    setPreviewLoading(false);
  };

  const copyVariable = (varName) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    message.success(`Copied {{${varName}}}`);
  };

  return (
    <Spin spinning={loading} indicator={antIcon}>
      <div className="pb-10">
        <div className="mb-6">
          <h3 className="font-bold mb-4" style={{ fontSize: "18px" }}>
            Notification Templates
          </h3>

          <div className="bg-white p-4 rounded-lg mb-6 space-y-4">
            <div>
              <label className="block font-medium mb-2">Event Type</label>
              <Select
                showSearch
                value={selectedEvent}
                onChange={setSelectedEvent}
                options={eventTypes.map((e) => ({ label: e, value: e }))}
                style={{ width: "100%" }}
                placeholder="Select event type"
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
              />
            </div>

            <div>
              <label className="block font-medium mb-2">Channel</label>
              <div className="flex gap-2 flex-wrap">
                {channels.map((ch) => (
                  <Button
                    key={ch}
                    type={selectedChannel === ch ? "primary" : "default"}
                    onClick={() => setSelectedChannel(ch)}
                  >
                    {ch}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-lg">
          {isEditing ? (
            <div className="space-y-4">
              <div className="flex gap-2 justify-end pb-4 border-b border-gray-200">
                <Button onClick={() => setVariablesModalOpen(true)}>
                  View Variables
                </Button>
                <Button onClick={() => setPreviewModalOpen(true)}>
                  Preview
                </Button>
              </div>

              {validationErrors.length > 0 && (
                <Alert
                  message="Validation Errors"
                  description={
                    <ul className="space-y-1 mb-0">
                      {validationErrors.map((err, idx) => (
                        <li key={idx}>• {err}</li>
                      ))}
                    </ul>
                  }
                  type="error"
                  closable
                />
              )}

              <div>
                <label className="block font-medium mb-2">Title</label>
                <Input
                  size="large"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="Template title with {{variables}}"
                />
              </div>

              <div>
                <label className="block font-medium mb-2">Body</label>
                <SimpleEditor
                  value={formData.body}
                  onChange={(html) => {
                    setFormData({ ...formData, body: html });
                    validateTemplate();
                  }}
                  placeholder="Type template body with {{variables}}"
                />
              </div>

              <Space className="w-full justify-between">
                <Button size="large" onClick={handleDiscard}>
                  Discard
                </Button>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleSave}
                  disabled={validationErrors.length > 0}
                >
                  Save
                </Button>
              </Space>
            </div>
          ) : (
            <div className="space-y-4">
              {currentTemplate ? (
                <>
                  <ul className="mobile-list-flex mb-4">
                    <li>
                      <span>Title:</span>
                      <span>{currentTemplate.title}</span>
                    </li>
                    <li>
                      <span>Status:</span>
                      <span>
                        {currentTemplate?.rule?.isActive
                          ? "Active"
                          : "Inactive"}
                      </span>
                    </li>
                  </ul>

                  <div className="bg-gray-50 p-4 rounded overflow-auto max-h-48">
                    <div
                      dangerouslySetInnerHTML={{ __html: currentTemplate.body }}
                    />
                  </div>

                  <Button
                    type="primary"
                    size="large"
                    onClick={() => handleEdit(currentTemplate)}
                    block
                  >
                    Edit
                  </Button>
                </>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-600 mb-4">No template created yet</p>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => setIsEditing(true)}
                  >
                    Create Template
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal
        title="Preview Template"
        open={previewModalOpen}
        onCancel={() => setPreviewModalOpen(false)}
        footer={null}
        centered
        width={600}
      >
        <Spin spinning={previewLoading} indicator={antIcon}>
          <div className="space-y-4">
            {availableVars && availableVars.length > 0 ? (
              <>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {availableVars.map((varName) => (
                    <div key={varName}>
                      <label className="block font-medium mb-2 text-sm">
                        {varName}
                      </label>
                      <Input
                        size="large"
                        placeholder={`Enter ${varName}`}
                        value={previewVars[varName] || ""}
                        onChange={(e) =>
                          setPreviewVars({
                            ...previewVars,
                            [varName]: e.target.value,
                          })
                        }
                      />
                    </div>
                  ))}
                </div>

                <Button
                  onClick={handlePreview}
                  loading={previewLoading}
                  type="primary"
                  size="large"
                  block
                >
                  Generate Preview
                </Button>
              </>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No variables available for this template
              </p>
            )}

            {templatePreview && (
              <Card className="bg-blue-50 border border-blue-200 mt-4">
                <div className="space-y-4">
                  <div>
                    <p className="font-bold mb-3 text-base text-gray-800">
                      Title:
                    </p>
                    <p className="text-blue-900 font-medium text-lg p-3 bg-white rounded border border-blue-100">
                      {templatePreview.title}
                    </p>
                  </div>
                  <Divider style={{ margin: "12px 0" }} />
                  <div>
                    <p className="font-bold mb-3 text-base text-gray-800">
                      Body:
                    </p>
                    <div
                      className="text-blue-900 p-4 bg-white rounded border border-blue-100 max-h-64 overflow-y-auto"
                      style={{
                        lineHeight: "1.8",
                        fontSize: "15px",
                        whiteSpace: "pre-wrap",
                        wordWrap: "break-word",
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: templatePreview.body,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </Spin>
      </Modal>

      <Modal
        title="Available Notification Variables"
        open={variablesModalOpen}
        onCancel={() => {
          setVariablesModalOpen(false);
          setVarSearchText("");
        }}
        footer={null}
        centered
        width={600}
        bodyStyle={{ maxHeight: "60vh", overflowY: "auto" }}
      >
        <div className="space-y-4">
          <Input
            placeholder="Search variables..."
            value={varSearchText}
            onChange={(e) => setVarSearchText(e.target.value)}
            className="mb-2"
            size="large"
            allowClear
          />
          <div className="space-y-3">
            {getRelevantCategories().map((cat) => {
              const filteredVars = (cat.variables || []).filter(
                (v) =>
                  v.key.toLowerCase().includes(varSearchText.toLowerCase()) ||
                  v.description.toLowerCase().includes(varSearchText.toLowerCase())
              );
              if (filteredVars.length === 0) return null;
              return (
                <div
                  key={cat.category}
                  className="border border-gray-100 rounded-lg p-3 bg-gray-50/50"
                >
                  <h4 className="font-bold text-gray-700 mb-2 border-b border-gray-100 pb-1 text-xs uppercase tracking-wider">
                    {cat.category}
                  </h4>
                  <div className="grid grid-cols-1 gap-2">
                    {filteredVars.map((v) => (
                      <div
                        key={v.key}
                        className="bg-white p-2 rounded border border-gray-100 flex justify-between items-center text-xs"
                      >
                        <div className="space-y-0.5 max-w-[80%]">
                          <span className="font-mono text-blue-600 font-bold text-lg">{`{{${v.key}}}`}</span>
                          <p className="text-gray-400 mb-0 leading-normal">
                            {v.description}
                          </p>
                        </div>
                        <Button
                          type="primary"
                          ghost
                          size="small"
                          className="text-xs h-8"
                          icon={<CopyOutlined />}
                          onClick={() => copyVariable(v.key)}
                        >
                          Copy
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
    </Spin>
  );
};

export default NotificationTemplatesManager;
